import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button, Dialog, Select, Switch } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Loading, Tabs, useInterval } from '../../components/common';
import { pods, type MetricsRange, type Pod, type PodMetrics } from '../../api/pods';
import { errorMessage } from '../../lib/errors';
import { formatBytes } from '../../lib/format';
import { UsageChart } from '@nyce/admin-kit/console';
import { formatCores } from '../../lib/pods';

const RANGES: { value: MetricsRange; label: string }[] = [
  { value: '1h', label: '1 h' },
  { value: '6h', label: '6 h' },
  { value: '24h', label: '24 h' },
  { value: '7d', label: '7 d' },
];

/** Cloud Monitoring samples once a minute; this follows them closely enough. */
export const METRICS_REFRESH_MS = 30_000;

type Scope = 'pod' | 'deployment';

/**
 * CPU and memory of one pod from Cloud Monitoring (GKE system metrics, ~1 min delay): usage per container against its
 * request and limit. A pod of a deployment can also show every pod the deployment had in the range, so the history
 * does not end at the last restart or rollout. Auto-refreshes every 30 s (paused in a hidden tab); a refetch keeps the
 * previous charts (dimmed) instead of flashing a spinner.
 */
export function PodMetricsDialog({
  environmentId,
  pod,
  onClose,
}: {
  environmentId: string;
  pod: Pod;
  onClose: () => void;
}) {
  const [range, setRange] = useState<MetricsRange>('1h');
  const [scope, setScope] = useState<Scope>('pod');
  const deployment = scope === 'deployment' ? pod.deployment : null;
  const [auto, setAuto] = useState(true);
  const [container, setContainer] = useState(pod.containers[0]?.name ?? '');
  const [data, setData] = useState<PodMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const id = ++seq.current;
    setLoading(true);
    try {
      const r = await pods.metrics(
        environmentId,
        pod.namespace,
        deployment ? { deployment } : { pod: pod.name },
        range
      );
      if (id !== seq.current) return;
      setData(r);
      setError(null);
    } catch (e) {
      if (id === seq.current) setError(errorMessage(e));
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, [environmentId, pod.namespace, pod.name, deployment, range]);
  useEffect(() => {
    void load();
  }, [load]);
  useInterval(() => void load(), METRICS_REFRESH_MS, auto);

  const names = data?.containers.map((c) => c.name) ?? [];
  const shown = data?.containers.find((c) => c.name === container) ?? data?.containers[0];

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={860}
      title={`Metrics · ${pod.name}`}
      footer={
        <>
          <Switch checked={auto} onChange={setAuto} label="Auto-refresh 30 s" />
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={loading}
            onClick={() => void load()}
          >
            Refresh
          </Button>
        </>
      }
    >
      <div className="oph-usage-bar">
        <Tabs value={range} onChange={setRange} options={RANGES} />
        {pod.deployment && (
          <Select
            aria-label="Pods"
            value={scope}
            onChange={(v) => setScope(v as Scope)}
            options={[
              { value: 'pod', label: 'This pod' },
              { value: 'deployment', label: `All pods of ${pod.deployment}` },
            ]}
          />
        )}
        {names.length > 1 && (
          <Select
            aria-label="Container"
            value={shown?.name ?? ''}
            onChange={setContainer}
            options={names.map((n) => ({ value: n, label: n }))}
          />
        )}
      </div>
      <ErrorBox error={error} />
      {!data && loading && <Loading loading />}
      {data && !data.containers.length && (
        <EmptyState
          title={data.deployment ? `No metrics for ${data.deployment}` : 'No metrics for this pod'}
          hint="Cloud Monitoring has no samples in this range yet (they arrive about a minute after a pod starts)."
        />
      )}
      {shown && data && (
        <div className={`oph-usage-charts${loading ? ' oph-usage-stale' : ''}`} data-testid="pod-metrics">
          <UsageChart
            title={names.length > 1 ? `CPU · ${shown.name}` : 'CPU'}
            series={shown.cpu}
            from={data.from}
            to={data.to}
            stepSeconds={data.stepSeconds}
            format={formatCores}
          />
          <UsageChart
            title={names.length > 1 ? `Memory · ${shown.name}` : 'Memory'}
            series={shown.memory}
            from={data.from}
            to={data.to}
            stepSeconds={data.stepSeconds}
            format={formatBytes}
          />
          <p className="nyk-ui-hint">
            Cloud Monitoring,{' '}
            {data.stepSeconds >= 3600 ? `${data.stepSeconds / 3600} h` : `${data.stepSeconds / 60} min`}{' '}
            averages. Memory is the non-evictable working set, the part the limit (OOM kill) applies to.
            {data.deployment &&
              ` Every pod of ${data.deployment} in the range, earlier ones too: per step the highest pod, so restarts and rollouts do not break the history.`}
          </p>
        </div>
      )}
    </Dialog>
  );
}
